import React from 'react';
import { Card, CardContent } from './ui/card';
import { Award, Handshake, Users, TrendingUp, Lightbulb, Sparkles } from 'lucide-react';
import { history, values, companyInfo } from '../mockData';

const iconMap = {
  'award': Award,
  'handshake': Handshake,
  'users': Users,
  'trending-up': TrendingUp,
  'lightbulb': Lightbulb
};

const HistoryValuesSection = () => {
  return (
    <section id="history-values" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gray-100 rounded-full blur-3xl opacity-30"></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
          <span className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg inline-flex items-center gap-2">
            <Sparkles size={16} />
            {history.title}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
            Notre histoire et
            <span className="block mt-2 bg-gradient-to-r from-yellow-500 to-yellow-400 bg-clip-text text-transparent">
              nos valeurs fondamentales
            </span>
          </h2>
        </div>

        {/* History Section */}
        <div className="max-w-5xl mx-auto mb-24">
          <Card className="border-2 border-yellow-100 overflow-hidden shadow-2xl bg-gradient-to-br from-white to-blue-50">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-5 gap-0">
                {/* Left side - Stats with gradient */}
                <div className="md:col-span-2 bg-gradient-to-br from-yellow-500 via-yellow-400 to-yellow-500 p-12 flex flex-col justify-center items-center text-gray-900 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 animate-pulse"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                  
                  <div className="relative z-10 text-center space-y-8">
                    <div className="space-y-3">
                      <div className="text-7xl font-bold drop-shadow-lg">{companyInfo.foundedYear}</div>
                      <div className="text-lg font-semibold">Année de fondation</div>
                    </div>

                    <div className="h-px bg-white/30 w-20 mx-auto"></div>

                    <div className="space-y-2">
                      <div className="text-5xl font-bold">{companyInfo.founderExperience}</div>
                      <div className="text-sm opacity-90">Ans d'expérience</div>
                    </div>
                  </div>
                </div>

                {/* Right side - Content */}
                <div className="md:col-span-3 p-12 space-y-6 bg-white">
                  <div className="space-y-4">
                    <h3 className="text-3xl font-bold text-gray-900">Notre Histoire</h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {history.story}
                    </p>
                  </div>

                  <div className="pt-6 border-t border-gray-100">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-yellow-500 to-yellow-400 p-4 rounded-xl shadow-lg">
                        <Award className="text-white" size={32} />
                      </div>
                      <div>
                        <div className="font-bold text-2xl text-gray-900">{companyInfo.founder}</div>
                        <div className="text-yellow-600 font-semibold text-lg">{companyInfo.founderTitle}</div>
                        <div className="text-gray-600 mt-1">Fondateur</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Values Section */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-3">
              NOS VALEURS
            </h3>
            <p className="text-xl text-gray-600">Les principes qui guident notre action au quotidien</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const IconComponent = iconMap[value.icon];
              return (
                <Card
                  key={value.id}
                  className="group relative overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-yellow-200 cursor-pointer bg-white"
                >
                  {/* Gradient background on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  
                  <CardContent className="p-8 space-y-4 relative z-10">
                    {/* Number badge */}
                    <div className="absolute top-6 right-6 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-400 flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>

                    {/* Icon */}
                    <div className="bg-gradient-to-br from-yellow-500 to-yellow-400 w-16 h-16 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                      <IconComponent className="text-white" size={30} />
                    </div>

                    {/* Content */}
                    <div className="space-y-3">
                      <h4 className="text-xl font-bold text-gray-900 group-hover:text-yellow-600 transition-colors">
                        {value.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {value.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HistoryValuesSection;
